"use client";

import { useState, useRef } from "react";
import dynamic from "next/dynamic";
import { Link } from "@/components/LocaleLink";
import { useLocaleRouter } from "@/lib/hooks/useLocaleRouter";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ArrowRight, Upload, FileText, X, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { useAuthStore } from "@/lib/store";
import { resumes as resumesApi } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { RealATSTeaser, type RealATSTeaserData } from "@/components/ats/RealATSTeaser";
import { anonSessionHeader, setAnonSessionId } from "@/lib/anonSession";
import { executeRecaptcha } from "@/lib/recaptcha";
import { RecaptchaLegalNotice } from "@/components/RecaptchaLegalNotice";
import { toast } from "sonner";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000";
import { SmoothScroll } from "@/components/landing-v2/SmoothScroll";
import { MagneticButton } from "@/components/landing-v2/MagneticButton";
import { WordReveal } from "@/components/landing-v2/WordReveal";
import { JourneyThread } from "@/components/landing-v2/JourneyThread";
import { CvScanSection } from "@/components/landing-v2/CvScanSection";

// Three.js hero is heavy (~130KB) — lazy-load and skip SSR
const HeroCanvas = dynamic(() => import("@/components/landing-v2/HeroCanvas").then(m => m.HeroCanvas), {
  ssr: false
});
export default function ATSLandingPage() {
  const {
    locale
  } = useTranslation();
  const router = useLocaleRouter();
  const {
    isAuthenticated,
    accessToken,
    _hasHydrated
  } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [jobTitle, setJobTitle] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [progress, setProgress] = useState(0);
  const [realResult, setRealResult] = useState<RealATSTeaserData | null>(null);
  const t = {
    fr: {
      nav: {
        work: "Analyse",
        pricing: "Tarifs",
        login: "Connexion",
        dashboard: "Dashboard"
      },
      hero: {
        kicker: "ANALYSE ATS · SANS INSCRIPTION",
        words: ["Ton", "CV,", "passé", "au", "filtre", "ATS", "des", "recruteurs."],
        accentIndex: 5,
        sub: "L'analyse CV ATS consiste à tester ton CV contre les mêmes filtres qu'utilisent les recruteurs avant tout regard humain. 97,8% du Fortune 500 utilise un ATS en 2025 (Jobscan). Velyq te rend en 30 secondes un score ATS chiffré, la liste des mots-clés manquants face à l'offre, le verdict sur le titre de poste et le format parsable. Gratuit, sans inscription.",
        cta: "Analyser mon CV",
        ctaLabel: "COMMENCER",
        scroll: "Défile"
      },
      journey: {
        intro: "Trois étapes. Quelques secondes entre ton CV et un rapport clair.",
        steps: [{
          num: "01",
          title: "Tu déposes ton CV.",
          body: "PDF ou Word. On lit la structure, les sections, les mots-clés. Rien n'est stocké si tu n'as pas de compte."
        }, {
          num: "02",
          title: "On croise avec l'offre.",
          body: "Colle la description du poste. Notre modèle identifie les compétences critiques, les hard skills obligatoires et les bonus, puis compare."
        }, {
          num: "03",
          title: "Tu obtiens un rapport actionnable.",
          body: "Score global, sous-scores par dimension (mots-clés, format, structure, densité). Pour chaque lacune, une suggestion concrète de réécriture."
        }]
      },
      scan: {
        kicker: "EN DÉTAIL",
        title: "Analyse ATS détaillée : ce que le scanner repère sur ton CV.",
        annotations: [{
          id: "a1",
          num: "01",
          label: "LISIBILITÉ MACHINE",
          value: "Format propre, polices ATS-safe",
          x: -42,
          y: 8
        }, {
          id: "a2",
          num: "02",
          label: "MOTS-CLÉS",
          value: "7 / 12 présents sur 12 critiques",
          x: 105,
          y: 22
        }, {
          id: "a3",
          num: "03",
          label: "STRUCTURE",
          value: "Sections clairement identifiées",
          x: -42,
          y: 48
        }, {
          id: "a4",
          num: "04",
          label: "DENSITÉ",
          value: "Verbes d'action, résultats chiffrés",
          x: 105,
          y: 60
        }, {
          id: "a5",
          num: "05",
          label: "SENIORITÉ",
          value: "Niveau sénior détecté",
          x: -36,
          y: 86
        }]
      },
      form: {
        kicker: "TESTE MAINTENANT",
        title: "Test ATS de ton CV : balance-le, colle l'offre, reçois ton score ATS.",
        uploadLabel: "Ton CV",
        uploadHint: "PDF ou DOCX, 5 Mo max",
        uploadButton: "Choisir un fichier",
        jobTitle: "Poste visé",
        jobTitlePlaceholder: "Ex. Consultant junior, Chef de projet...",
        company: "Entreprise (optionnel)",
        companyPlaceholder: "Ex. McKinsey, L'Oréal...",
        jobDesc: "Description de l'offre",
        jobDescPlaceholder: "Colle ici le texte de l'offre...",
        analyze: "Lancer l'analyse",
        analyzing: "Analyse en cours...",
        tooShort: "Description trop courte (50 caractères min)",
        steps: ["Lecture de ton CV...", "Extraction des compétences...", "Analyse des mots-clés de l'offre...", "Comparaison CV / Offre...", "Génération des recommandations..."]
      },
      uses: {
        kicker: "QUI UTILISE UN ATS ?",
        title: "Qui filtre ton CV avec un ATS ?",
        intro: "Workday, Taleo, Greenhouse, iCIMS, Lever — la majorité des grandes entreprises françaises et des cabinets conseil passent tous les CV reçus dans un ATS avant qu'un humain en voie un seul. Tester ton CV contre un ATS n'est plus optionnel.",
        items: [{
          type: "Grands groupes",
          examples: "L'Oréal · LVMH · BNP Paribas · TotalEnergies"
        }, {
          type: "Cabinets conseil",
          examples: "McKinsey · BCG · Bain · Accenture · Capgemini"
        }, {
          type: "Scale-ups",
          examples: "Doctolib · BlaBlaCar · Back Market · Qonto"
        }, {
          type: "Finance",
          examples: "Rothschild · Lazard · Goldman Sachs · JP Morgan"
        }]
      },
      resources: {
        kicker: "RESSOURCES · POUR ALLER PLUS LOIN",
        title: "Pour creuser : nos guides ATS.",
        intro: "3 articles qui détaillent ce que fait un ATS en 2026, comment le passer et ce qui change d'un métier à l'autre. Données 2025 sourcées, pas du recyclé SEO.",
        items: [{
          label: "GUIDE COMPLET",
          title: "Passer un ATS en 2026 : ce qui a vraiment changé",
          description: "Parsing sémantique, détection des CV IA, skills-based hiring — les 7 règles qui comptent en 2026 et les erreurs de 2022 à oublier.",
          href: "/blog/comment-passer-ats-cv-2026"
        }, {
          label: "DIAGNOSTIC",
          title: "10 erreurs qui plantent ton score ATS",
          description: "Titre non aligné, CV trop « IA », hidden prompts inutiles… Classées par gravité réelle avec les stats Jobscan 2025.",
          href: "/blog/erreurs-cv-ats-score-faible"
        }, {
          label: "PAR MÉTIER",
          title: "CV ATS par métier : tech, finance, consulting",
          description: "Les grilles de lecture ATS réelles pour les 3 secteurs les plus codifiés — écoles cibles, signaux pondérés, mots-clés exacts.",
          href: "/blog/cv-ats-tech-finance-consulting"
        }],
        crosslinksLabel: "Et pour la préparation entretien par secteur :",
        crosslinks: [{
          label: "Entretien tech",
          href: "/entretien-tech"
        }, {
          label: "Entretien finance",
          href: "/entretien-finance"
        }, {
          label: "Entretien consulting",
          href: "/entretien-consulting"
        }, {
          label: "Tous les articles",
          href: "/blog"
        }]
      },
      faq: {
        kicker: "QUESTIONS · FRÉQUENTES",
        title: "Ce que les gens nous demandent.",
        items: [{
          q: "Mon CV peut vraiment être rejeté automatiquement ?",
          a: "Oui. Les ATS attribuent un score à chaque CV. Si ton score est trop bas, ton CV n'est jamais vu par un humain — même si tu es parfaitement qualifié."
        }, {
          q: "Comment fonctionne l'analyse ?",
          a: "Notre IA lit ton CV et l'offre, identifie les compétences et mots-clés attendus, puis produit un score de compatibilité et des recommandations précises."
        }, {
          q: "Mes données sont-elles stockées ?",
          a: "Sans compte : analyse en mémoire, rien n'est conservé. Avec compte : stockage RGPD en Europe (GCP europe-west1), suppression à la demande."
        }, {
          q: "C'est gratuit ?",
          a: "Oui pour la première analyse. 1 analyse/mois en Free. Illimité + suggestions de réécriture en Premium (9,99€/mois)."
        }]
      }
    },
    en: {
      nav: {
        work: "Analysis",
        pricing: "Pricing",
        login: "Login",
        dashboard: "Dashboard"
      },
      hero: {
        kicker: "ATS ANALYZER · NO SIGN-UP",
        words: ["Your", "resume,", "through", "the", "ATS", "recruiter", "filter."],
        accentIndex: 4,
        sub: "ATS resume analysis means testing your CV against the same filters recruiters use before any human reads it. 97.8% of Fortune 500 companies now use an ATS (Jobscan, 2025). Velyq returns in 30 seconds a scored ATS score, the list of missing keywords vs. the job offer, the job-title verdict, and whether your format is parsable. Free, no signup.",
        cta: "Analyze my resume",
        ctaLabel: "START",
        scroll: "Scroll"
      },
      journey: {
        intro: "Three steps. Seconds between your resume and a clear report.",
        steps: [{
          num: "01",
          title: "You drop your resume.",
          body: "PDF or Word. We read the structure, sections, keywords. Nothing is stored without an account."
        }, {
          num: "02",
          title: "We cross-check with the job.",
          body: "Paste the job description. Our model identifies critical skills, mandatory hard skills, bonuses, then compares."
        }, {
          num: "03",
          title: "You get an actionable report.",
          body: "Overall score, sub-scores by dimension (keywords, format, structure, density). Each gap gets a concrete rewrite suggestion."
        }]
      },
      scan: {
        kicker: "UNDER THE HOOD",
        title: "Detailed ATS analysis: what the scanner picks up on your resume.",
        annotations: [{
          id: "a1",
          num: "01",
          label: "MACHINE READABILITY",
          value: "Clean format, ATS-safe fonts",
          x: -42,
          y: 8
        }, {
          id: "a2",
          num: "02",
          label: "KEYWORDS",
          value: "7 / 12 critical terms present",
          x: 105,
          y: 22
        }, {
          id: "a3",
          num: "03",
          label: "STRUCTURE",
          value: "Clearly identified sections",
          x: -42,
          y: 48
        }, {
          id: "a4",
          num: "04",
          label: "DENSITY",
          value: "Action verbs, measurable results",
          x: 105,
          y: 60
        }, {
          id: "a5",
          num: "05",
          label: "SENIORITY",
          value: "Senior level detected",
          x: -36,
          y: 86
        }]
      },
      form: {
        kicker: "TRY IT NOW",
        title: "ATS resume test: drop your CV, paste the job, get your ATS score.",
        uploadLabel: "Your resume",
        uploadHint: "PDF or DOCX, 5MB max",
        uploadButton: "Choose a file",
        jobTitle: "Target role",
        jobTitlePlaceholder: "E.g. Junior Consultant, Project Manager...",
        company: "Company (optional)",
        companyPlaceholder: "E.g. McKinsey, Google...",
        jobDesc: "Job description",
        jobDescPlaceholder: "Paste the job posting here...",
        analyze: "Start analysis",
        analyzing: "Analyzing...",
        tooShort: "Description too short (50 chars min)",
        steps: ["Reading your resume...", "Extracting skills...", "Analyzing job keywords...", "Comparing resume / job...", "Generating recommendations..."]
      },
      uses: {
        kicker: "WHO USES AN ATS?",
        title: "Who filters your resume with an ATS?",
        intro: "Workday, Taleo, Greenhouse, iCIMS, Lever — most large companies and consulting firms run every resume through an ATS before any human sees one. Testing your resume against an ATS is no longer optional.",
        items: [{
          type: "Large corporations",
          examples: "Google · Amazon · Microsoft · Apple"
        }, {
          type: "Consulting firms",
          examples: "McKinsey · BCG · Bain · Accenture · Deloitte"
        }, {
          type: "Scale-ups",
          examples: "Stripe · Figma · Notion · Airbnb"
        }, {
          type: "Finance",
          examples: "Goldman Sachs · JP Morgan · Morgan Stanley"
        }]
      },
      resources: {
        kicker: "RESOURCES · GO DEEPER",
        title: "Want more? Our ATS guides.",
        intro: "Three articles that break down what an ATS really does in 2026, how to pass it, and how it shifts from one industry to another. 2025-sourced data, no recycled SEO.",
        items: [{
          label: "FULL GUIDE",
          title: "Passing an ATS in 2026: what actually changed",
          description: "Semantic parsing, AI-resume detection, skills-based hiring — the 7 rules that matter in 2026 and the 2022 tricks to forget.",
          href: "/blog/comment-passer-ats-cv-2026"
        }, {
          label: "DIAGNOSIS",
          title: "10 mistakes that tank your ATS score",
          description: "Misaligned title, too-AI resume, useless hidden prompts… Ranked by real severity using 2025 Jobscan stats.",
          href: "/blog/erreurs-cv-ats-score-faible"
        }, {
          label: "BY INDUSTRY",
          title: "ATS resume by industry: tech, finance, consulting",
          description: "The actual ATS grids for the 3 most codified sectors — target schools, weighted signals, exact keywords.",
          href: "/blog/cv-ats-tech-finance-consulting"
        }],
        crosslinksLabel: "And for interview prep by sector:",
        crosslinks: [{
          label: "Tech interview",
          href: "/entretien-tech"
        }, {
          label: "Finance interview",
          href: "/entretien-finance"
        }, {
          label: "Consulting interview",
          href: "/entretien-consulting"
        }, {
          label: "All articles",
          href: "/blog"
        }]
      },
      faq: {
        kicker: "FREQUENTLY · ASKED",
        title: "What people ask us most.",
        items: [{
          q: "Can my resume really be rejected automatically?",
          a: "Yes. ATS score every resume. If your score is too low, your resume is never seen by a human — even if you're perfectly qualified."
        }, {
          q: "How does the analysis work?",
          a: "Our AI reads your resume and the job offer, identifies the skills and keywords sought, then produces a compatibility score and precise recommendations."
        }, {
          q: "Is my data stored?",
          a: "No account: in-memory analysis, nothing kept. With an account: GDPR-compliant storage in Europe (GCP europe-west1), deletion on request."
        }, {
          q: "Is it free?",
          a: "Yes for the first analysis. 1 per month on Free. Unlimited + rewrite suggestions on Premium (€9.99/month)."
        }]
      }
    }
  };
  const content = locale === "fr" ? t.fr : t.en;
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCvFile(file);
  };
  const handleAnalyze = async () => {
    if (!cvFile) {
      toast.error(locale === "fr" ? "Sélectionne un CV" : "Please select a CV");
      return;
    }
    if (!jobDescription || jobDescription.trim().length < 50) {
      toast.error(locale === "fr" ? "Description du poste trop courte (50 caractères min)" : "Job description too short (50 chars min)");
      return;
    }
    // Guests must tick the GDPR consent box — the backend 400s
    // (consent_required) otherwise. Was only "gated" by a non-existent CSS
    // class on the button, so users submitted and burned a 12s cold-start
    // round-trip just to learn about the checkbox. 9/10 of the daily
    // guest-analyze 400s came from this.
    if (!isAuthenticated && !consentAccepted) {
      toast.error(locale === "fr" ? "Coche la case de consentement pour lancer l'analyse" : "Tick the consent box to run the analysis");
      document.getElementById("ats-consent")?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
      return;
    }
    setIsAnalyzing(true);
    setShowResults(false);
    setRealResult(null);
    setProgress(0);

    // === Branch 1: logged-in user → authenticated flow ===
    if (isAuthenticated && accessToken) {
      try {
        const progressInterval = setInterval(() => {
          setProgress(prev => Math.min(prev + 8, 90));
        }, 600);

        // Upload CV as a real Resume
        const resume = (await resumesApi.upload(accessToken, cvFile)) as {
          id: string;
        };

        // Run ATS analysis against the uploaded resume
        const analysis = await resumesApi.atsAnalysis(accessToken, resume.id, {
          job_description: jobDescription,
          job_title: jobTitle || undefined
        });
        clearInterval(progressInterval);
        setProgress(100);

        // Redirect to the full authenticated ATS detail page
        router.push(`/resumes/${resume.id}/ats/${analysis.id}`);
        return;
      } catch (err) {
        console.error("authenticated analysis failed:", err);
        const {
          ApiError
        } = await import("@/lib/api");
        if (err instanceof ApiError && err.upgradeRequired) {
          // Free-plan quota gate (1 CV lifetime) — surface the Premium CTA
          // instead of a generic failure.
          toast.error(err.message, {
            action: {
              label: locale === "fr" ? "Passer à Premium" : "Go Premium",
              onClick: () => router.push("/pricing")
            },
            duration: 8000
          });
        } else {
          toast.error(locale === "fr" ? "Erreur lors de l'analyse. Réessaie." : "Analysis failed. Try again.");
        }
        setIsAnalyzing(false);
        return;
      }
    }

    // === Branch 2: anonymous user → existing guest flow ===
    try {
      // 1. Get reCAPTCHA token (best-effort — backend accepts null for now)
      let recaptchaToken = "";
      try {
        const token = await executeRecaptcha("ats_guest_analyze");
        recaptchaToken = token ?? "";
      } catch (err_1) {
        console.warn("recaptcha unavailable, proceeding without:", err_1);
      }

      // 2. Build multipart form
      const formData = new FormData();
      formData.append("cv", cvFile);
      formData.append("job_title", jobTitle || "");
      formData.append("company_name", companyName || "");
      formData.append("job_description", jobDescription);
      formData.append("recaptcha_token", recaptchaToken);
      formData.append("consent_accepted", consentAccepted ? "true" : "false");

      // 3. POST to backend — animate progress while waiting
      const progressInterval_0 = setInterval(() => {
        setProgress(prev_0 => Math.min(prev_0 + 8, 90));
      }, 600);
      const res = await fetch(`${API_URL}/api/v1/ats/guest-analyze`, {
        method: "POST",
        body: formData,
        credentials: "include",
        // Echo the stored anon session id (cookieless funnel). Empty on the very
        // first analysis; present on a repeat so quota tracks the same session.
        headers: {
          ...anonSessionHeader()
        }
      });
      clearInterval(progressInterval_0);
      setProgress(100);
      if (!res.ok) {
        const errorBody = await res.json().catch(() => ({}));
        const code = errorBody?.detail?.code;
        const msg = errorBody?.detail?.message || (locale === "fr" ? "Analyse impossible. Réessaie." : "Analysis failed. Try again.");
        if (code === "guest_rate_limited") {
          toast.error(locale === "fr" ? "Trop d'analyses depuis ton IP. Crée un compte pour continuer." : "Too many analyses from your IP. Sign up to continue.");
        } else {
          toast.error(msg);
        }
        setIsAnalyzing(false);
        return;
      }
      const data: RealATSTeaserData = await res.json();
      // Cookieless funnel: persist the anon session id so email-claim / Google
      // signup can prove ownership without the third-party cookie.
      setAnonSessionId(data.anonymous_session_id);
      setRealResult(data);
      setShowResults(true);
      // Funnel: guest ran the real analysis — important conversion signal
      (await import("@/lib/analytics")).trackEvent("ats_analysis_completed", {
        guest: true,
        score: data.score,
        has_job_title: Boolean(jobTitle?.trim())
      });
    } catch (err_0) {
      console.error("guest-analyze failed:", err_0);
      toast.error(locale === "fr" ? "Erreur réseau. Réessaie." : "Network error. Try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // For anonymous users, consent must also be checked before submit
  const canAnalyze = Boolean(cvFile && jobDescription.trim().length >= 50 && (isAuthenticated || consentAccepted));
  return <div className="landing-v2">
      <SmoothScroll />

      {/* Nav */}
      <nav className="l2-nav">
        <Link href="/" aria-label="Velyq home">
          <Logo size="sm" />
        </Link>
        <div className="l2-nav-links">
          <a href="#form">{content.nav.work}</a>
          <Link href="/pricing">{content.nav.pricing}</Link>
          {_hasHydrated && isAuthenticated ? <Link href="/dashboard">
              <Button size="sm">{content.nav.dashboard}</Button>
            </Link> : _hasHydrated ? <Link href="/login">{content.nav.login}</Link> : null}
          <LanguageSwitcher />
        </div>
      </nav>

      {/* Hero */}
      <section className="l2-hero l2-grid-bg">
        <HeroCanvas />
        <div className="l2-hero-content">
          <p className="l2-mono" style={{
          marginBottom: 32
        }}>
            [ {content.hero.kicker} ]
          </p>
          <WordReveal as="h1" className="l2-display l2-display-xl" delay={0.3} renderWord={(w, i) => i === content.hero.accentIndex ? <em className="l2-accent-italic">{w}</em> : <>{w}</>}>
            {content.hero.words.join(" ")}
          </WordReveal>
          <p style={{
          maxWidth: 640,
          margin: "40px auto 48px",
          fontSize: 17,
          lineHeight: 1.55,
          color: "var(--l2-fg-dim)"
        }}>
            {content.hero.sub}
          </p>
          <MagneticButton label={content.hero.ctaLabel} onClick={() => {
          document.getElementById("form")?.scrollIntoView({
            behavior: "smooth"
          });
        }}>
            <span>{content.hero.cta}</span>
            <ArrowRight size={14} />
          </MagneticButton>
        </div>
        <div className="l2-scroll-hint">↓ {content.hero.scroll}</div>
      </section>

      {/* Journey */}
      <JourneyThread intro={content.journey.intro} steps={content.journey.steps} />

      {/* CV Scan pin */}
      <CvScanSection kicker={content.scan.kicker} title={content.scan.title} annotations={content.scan.annotations} />

      {/* Form Section */}
      <section id="form" className="l2-section" style={{
      paddingTop: 40
    }}>
        <div style={{
        maxWidth: 900,
        margin: "0 auto"
      }}>
          <p className="l2-mono" style={{
          marginBottom: 24
        }}>
            [ {content.form.kicker} ]
          </p>
          <h2 className="l2-display l2-display-lg" style={{
          marginBottom: 56
        }}>
            {content.form.title}
          </h2>

          {!isAnalyzing && !showResults && <div style={{
          display: "grid",
          gap: 28,
          padding: 40,
          borderRadius: 16,
          border: "1px solid var(--l2-fg-faint)",
          background: "var(--l2-bg-2)"
        }}>
              {/* CV Upload */}
              <div>
                <label className="l2-mono" style={{
              display: "block",
              marginBottom: 10
            }}>
                  {content.form.uploadLabel}
                </label>
                <div onClick={() => fileInputRef.current?.click()} style={{
              border: "1px dashed var(--l2-fg-faint)",
              borderColor: cvFile ? "var(--l2-accent)" : undefined,
              background: cvFile ? "var(--l2-accent-soft)" : "transparent",
              borderRadius: 10,
              padding: 28,
              cursor: "pointer",
              textAlign: "center",
              transition: "all 0.3s var(--l2-ease-out)"
            }}>
                  <input ref={fileInputRef} type="file" accept=".pdf,.docx" className="hidden" style={{
                display: "none"
              }} onChange={handleFileChange} />
                  {cvFile ? <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12
              }}>
                      <FileText size={22} style={{
                  color: "var(--l2-accent)"
                }} />
                      <div style={{
                  textAlign: "left"
                }}>
                        <p style={{
                    fontWeight: 500
                  }}>{cvFile.name}</p>
                        <p style={{
                    fontSize: 12,
                    color: "var(--l2-fg-dim)"
                  }}>
                          {(cvFile.size / 1024 / 1024).toFixed(2)} Mo
                        </p>
                      </div>
                      <button type="button" onClick={e_0 => {
                  e_0.stopPropagation();
                  setCvFile(null);
                }} style={{
                  marginLeft: 12,
                  background: "transparent",
                  border: "none",
                  color: "var(--l2-fg-dim)",
                  cursor: "pointer"
                }}>
                        <X size={16} />
                      </button>
                    </div> : <>
                      <Upload size={28} style={{
                  color: "var(--l2-fg-dim)",
                  margin: "0 auto 10px",
                  display: "block"
                }} />
                      <p style={{
                  fontWeight: 500,
                  marginBottom: 4
                }}>
                        {content.form.uploadButton}
                      </p>
                      <p style={{
                  fontSize: 12,
                  color: "var(--l2-fg-dim)"
                }}>
                        {content.form.uploadHint}
                      </p>
                    </>}
                </div>
              </div>

              <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16
          }} className="l2-form-grid">
                <div>
                  <label className="l2-mono" style={{
                display: "block",
                marginBottom: 10
              }}>
                    {content.form.jobTitle}
                  </label>
                  <Input placeholder={content.form.jobTitlePlaceholder} value={jobTitle} onChange={e_1 => setJobTitle(e_1.target.value)} maxLength={255} />
                </div>
                <div>
                  <label className="l2-mono" style={{
                display: "block",
                marginBottom: 10
              }}>
                    {content.form.company}
                  </label>
                  <Input placeholder={content.form.companyPlaceholder} value={companyName} onChange={e_2 => setCompanyName(e_2.target.value)} maxLength={255} />
                </div>
              </div>

              <div>
                <label className="l2-mono" style={{
              display: "block",
              marginBottom: 10
            }}>
                  {content.form.jobDesc} *
                </label>
                <Textarea placeholder={content.form.jobDescPlaceholder} value={jobDescription} onChange={e_3 => setJobDescription(e_3.target.value)} rows={6}
            // `[overflow-wrap:anywhere]` breaks long unbroken strings
            //  (a user pasted 1k+ chars without spaces and the textarea
            //  grew past the form width, see screenshot 2026-06-07).
            //  `max-w-full` re-anchors the textarea to its parent so
            //  `field-sizing-content` from the shadcn base class can't
            //  expand it horizontally either.
            className="max-h-[280px] max-w-full overflow-y-auto [overflow-wrap:anywhere]" />
                {jobDescription && jobDescription.length < 50 && <p style={{
              fontSize: 12,
              color: "oklch(0.75 0.18 60)",
              marginTop: 8,
              display: "flex",
              alignItems: "center",
              gap: 6
            }}>
                    <AlertTriangle size={12} />
                    {content.form.tooShort}
                  </p>}
              </div>

              {/* GDPR consent — must be checked before upload (anonymous users only).
                  Gated on _hasHydrated: server HTML always includes this block
                  (isAuthenticated is false server-side), so the first client
                  render must too — it only collapses post-hydration. */}
              {(!_hasHydrated || !isAuthenticated) && <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" id="ats-consent" checked={consentAccepted} onChange={e_4 => setConsentAccepted(e_4.target.checked)} className="mt-0.5 cursor-pointer" required />
                  <label htmlFor="ats-consent" className="cursor-pointer leading-relaxed">
                    {locale === "fr" ? <>J&apos;autorise Velyq à analyser mon CV via OpenAI / Gemini (États-Unis) à des fins d&apos;évaluation ATS. Données conservées 30 jours puis supprimées automatiquement. <a href="/privacy" target="_blank" className="underline">Politique de confidentialité</a>.</> : <>I consent to Velyq analyzing my CV via OpenAI / Gemini (United States) for ATS evaluation. Data kept for 30 days then automatically deleted. <a href="/privacy" target="_blank" className="underline">Privacy policy</a>.</>}
                  </label>
                </div>}

              <MagneticButton label={content.form.kicker} onClick={handleAnalyze} className={!canAnalyze ? "l2-magnet-disabled" : ""}>
                <span>{content.form.analyze}</span>
                <ArrowRight size={14} />
              </MagneticButton>
              {!canAnalyze && <p style={{
            fontSize: 11,
            color: "var(--l2-fg-dim)",
            textAlign: "center",
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            marginTop: -12
          }}>
                  {/* Name the ACTUAL missing step — consent was the silent
                      blocker behind 9/10 of the daily guest-analyze 400s. */}
                  {!cvFile || jobDescription.trim().length < 50 ? locale === "fr" ? "Ajoute un CV et une description (50+ caractères)" : "Add a resume and a description (50+ chars)" : locale === "fr" ? "Coche la case de consentement ci-dessus" : "Tick the consent box above"}
                </p>}
              <div style={{
            marginTop: 16,
            textAlign: "center"
          }}>
                <RecaptchaLegalNotice />
              </div>
            </div>}

          {isAnalyzing && <div style={{
          padding: 48,
          borderRadius: 16,
          border: "1px solid var(--l2-fg-faint)",
          background: "var(--l2-bg-2)"
        }}>
              <div style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: 24
          }}>
                <Loader2 size={32} style={{
              color: "var(--l2-accent)",
              animation: "spin 1s linear infinite"
            }} />
              </div>
              <h3 style={{
            textAlign: "center",
            marginBottom: 24,
            fontFamily: "var(--font-display)",
            fontSize: 22
          }}>
                {content.form.analyzing}
              </h3>
              <div style={{
            marginBottom: 24
          }}>
                {content.form.steps.map((step, i_0) => {
              const currentStep = Math.floor(progress / 20);
              const isComplete = i_0 < currentStep;
              const isCurrent = i_0 === currentStep;
              return <div key={i_0} style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                fontSize: 13,
                padding: "6px 0",
                color: isComplete || isCurrent ? "var(--l2-fg)" : "var(--l2-fg-faint)"
              }}>
                      {isComplete ? <CheckCircle2 size={16} style={{
                  color: "var(--l2-accent)",
                  flexShrink: 0
                }} /> : isCurrent ? <Loader2 size={16} style={{
                  color: "var(--l2-accent)",
                  animation: "spin 1s linear infinite",
                  flexShrink: 0
                }} /> : <div style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: "1px solid var(--l2-fg-faint)",
                  flexShrink: 0
                }} />}
                      {step}
                    </div>;
            })}
              </div>
              <div style={{
            width: "100%",
            background: "var(--l2-fg-faint)",
            borderRadius: 2,
            height: 2
          }}>
                <div style={{
              background: "var(--l2-accent)",
              height: 2,
              borderRadius: 2,
              width: `${progress}%`,
              boxShadow: "0 0 8px var(--l2-accent)",
              transition: "width 0.5s ease-out"
            }} />
              </div>
            </div>}

          {showResults && realResult && <RealATSTeaser data={realResult} />}
        </div>
      </section>

      {/* Who uses */}
      <section className="l2-section">
        <div style={{
        maxWidth: 1200,
        margin: "0 auto"
      }}>
          <p className="l2-mono" style={{
          marginBottom: 24
        }}>
            [ {content.uses.kicker} ]
          </p>
          <h2 className="l2-display l2-display-lg" style={{
          marginBottom: 24,
          maxWidth: 800
        }}>
            {content.uses.title}
          </h2>
          <p style={{
          maxWidth: 720,
          marginBottom: 40,
          fontSize: 16,
          lineHeight: 1.6,
          color: "var(--l2-fg-dim)"
        }}>
            {content.uses.intro}
          </p>
          <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 2,
          borderTop: "1px solid var(--l2-fg-faint)",
          borderLeft: "1px solid var(--l2-fg-faint)",
          marginTop: 40
        }}>
            {content.uses.items.map((c, i_1) => <div key={i_1} style={{
            padding: "28px 24px",
            borderRight: "1px solid var(--l2-fg-faint)",
            borderBottom: "1px solid var(--l2-fg-faint)",
            minHeight: 120
          }}>
                <p className="l2-mono" style={{
              marginBottom: 12,
              color: "var(--l2-accent)"
            }}>
                  [0{i_1 + 1}]
                </p>
                <p style={{
              fontFamily: "var(--font-display)",
              fontSize: 22,
              lineHeight: 1.1,
              marginBottom: 10,
              letterSpacing: "-0.02em"
            }}>
                  {c.type}
                </p>
                <p style={{
              fontSize: 12,
              color: "var(--l2-fg-dim)",
              lineHeight: 1.5
            }}>
                  {c.examples}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Resources — cross-links to ATS articles */}
      <section className="l2-section">
        <div style={{
        maxWidth: 1200,
        margin: "0 auto"
      }}>
          <p className="l2-mono" style={{
          marginBottom: 24
        }}>
            [ {content.resources.kicker} ]
          </p>
          <h2 className="l2-display l2-display-lg" style={{
          marginBottom: 24,
          maxWidth: 800
        }}>
            {content.resources.title}
          </h2>
          <p style={{
          maxWidth: 720,
          marginBottom: 48,
          fontSize: 16,
          lineHeight: 1.6,
          color: "var(--l2-fg-dim)"
        }}>
            {content.resources.intro}
          </p>
          <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 2,
          borderTop: "1px solid var(--l2-fg-faint)",
          borderLeft: "1px solid var(--l2-fg-faint)",
          marginBottom: 40
        }}>
            {content.resources.items.map((item, i_2) => <Link key={i_2} href={item.href} style={{
            display: "block",
            padding: "28px 24px",
            borderRight: "1px solid var(--l2-fg-faint)",
            borderBottom: "1px solid var(--l2-fg-faint)",
            minHeight: 200,
            textDecoration: "none",
            color: "inherit",
            transition: "background 0.2s ease"
          }} className="hover:bg-white/[0.02]">
                <p className="l2-mono" style={{
              marginBottom: 14,
              color: "var(--l2-accent)"
            }}>
                  [0{i_2 + 1}] · {item.label}
                </p>
                <p style={{
              fontFamily: "var(--font-display)",
              fontSize: 22,
              lineHeight: 1.15,
              marginBottom: 12,
              letterSpacing: "-0.02em"
            }}>
                  {item.title}
                </p>
                <p style={{
              fontSize: 13,
              color: "var(--l2-fg-dim)",
              lineHeight: 1.55,
              marginBottom: 16
            }}>
                  {item.description}
                </p>
                <p className="l2-mono" style={{
              fontSize: 11,
              color: "var(--l2-fg-faint)"
            }}>
                  LIRE →
                </p>
              </Link>)}
          </div>
          <div style={{
          paddingTop: 32,
          borderTop: "1px solid var(--l2-fg-faint)"
        }}>
            <p className="l2-mono" style={{
            marginBottom: 16,
            color: "var(--l2-fg-dim)",
            fontSize: 12
          }}>
              {content.resources.crosslinksLabel}
            </p>
            <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12
          }}>
              {content.resources.crosslinks.map((link, i_3) => <Link key={i_3} href={link.href} style={{
              display: "inline-block",
              padding: "8px 16px",
              border: "1px solid var(--l2-fg-faint)",
              borderRadius: 999,
              fontSize: 13,
              color: "var(--l2-fg-dim)",
              textDecoration: "none",
              transition: "all 0.2s ease"
            }} className="hover:border-[var(--l2-accent)] hover:text-[var(--l2-fg)]">
                  {link.label}
                </Link>)}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="l2-section">
        <div style={{
        maxWidth: 900,
        margin: "0 auto"
      }}>
          <p className="l2-mono" style={{
          marginBottom: 24
        }}>
            [ {content.faq.kicker} ]
          </p>
          <h2 className="l2-display l2-display-lg" style={{
          marginBottom: 56,
          maxWidth: 700
        }}>
            {content.faq.title}
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {content.faq.items.map((item_0, i_4) => <AccordionItem key={i_4} value={`faq-${i_4}`} style={{
            borderColor: "var(--l2-fg-faint)"
          }}>
                <AccordionTrigger style={{
              textAlign: "left",
              fontFamily: "var(--font-display)",
              fontSize: 20,
              letterSpacing: "-0.01em",
              color: "var(--l2-fg)",
              padding: "24px 0"
            }}>
                  {item_0.q}
                </AccordionTrigger>
                <AccordionContent style={{
              color: "var(--l2-fg-dim)",
              lineHeight: 1.6,
              fontSize: 15
            }}>
                  {item_0.a}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>
      </section>

      {/* Standard Velyq footer */}
      <Footer />
    </div>;
}